import React, { useCallback, useState } from 'react'
import { Strophe } from 'react-strophe'
import { getSessionId } from '@fs/zion-session'
import { Chat } from '../global/chatSettingsConfig'
import api from '../api/chat'
import { configureLogging } from '../global/chatConnectionHelper'
import chatLog from '../global/chat-log'
import { getUniqueId } from '../global/xmppPayloadHelper'
import { useClearAllUnreadMessagesFromCache } from './useUnreadMessageCache'
import orchestrationApi from '../api/orchestration'

/*
  When the Chat server connection has been established.
 */
const onConnect = async ({ status, connection, dispatch, fsUser }) => {
  if (status === Strophe.Status.CONNECTING) {
    chatLog('[Global] Connecting to the chat server...')
  } else if (status === Strophe.Status.ERROR) {
    chatLog('[Global] Error connecting to the chat server.')
    dispatch({ type: 'FAILED_TO_CONNECT', data: status })
  } else if (status === Strophe.Status.CONNFAIL) {
    chatLog('[Global] Unable to connect to the chat server.')
    dispatch({ type: 'FAILED_TO_CONNECT', data: status })
  } else if (status === Strophe.Status.AUTHFAIL) {
    chatLog('[Global] Authentication failed to the chat server.')
    dispatch({ type: 'FAILED_TO_CONNECT', data: status })
  } else if (status === Strophe.Status.DISCONNECTED) {
    chatLog('[Global] Disconnected from the chat server.')
    dispatch({ type: 'FAILED_TO_CONNECT', data: status })
  } else if (status === Strophe.Status.DISCONNECTING) {
    chatLog('[Global] Disconnecting to the chat server...')
  } else if (status === Strophe.Status.CONNECTED) {
    configureLogging({ connection })
    chatLog('[Global] Connected!')
    const user = {
      authcid: connection.authcid,
      authzid: connection.authzid,
      jid: connection.jid,
    }

    dispatch({ type: 'CHAT_LOGIN', connection, user })
    await api.messages.enableMessageCarbons({ connection, fromJid: user.authzid })

    // Fire and forget this log metric
    try {
      orchestrationApi.chatmetrics.logLogin({
        jabberResource: Strophe.getResourceFromJid(connection.jid),
        jid: user.authzid,
        displayName: fsUser.displayName,
      })
    } catch (e) {
      // ignoring
    }
  }

  return connection
}

export default function useChatServerConnection({ fsUser, dispatch }) {
  const [clearCache] = useClearAllUnreadMessagesFromCache()
  const [connection, setConnection] = useState(null)
  const tempConnection = new Strophe.Connection(Chat.Settings.WS_URL, { protocol: 'wss' })

  // Handle the connection initiation response
  const onConnectCallback = React.useCallback(
    (status) => {
      onConnect({ status, connection: tempConnection, dispatch, fsUser }).then((conn) => {
        setConnection(conn)
      })
    },
    [tempConnection, dispatch, fsUser]
  )

  // Create a connect method for others to use.
  const connect = useCallback(() => {
    const uniqueResourceId = `rtc-res-${getUniqueId()}`
    const chatUsername = fsUser.cisId
    const chatPassword = getSessionId()
    const username = `${chatUsername}@${Chat.Settings.DOMAIN}/${uniqueResourceId}`
    tempConnection.connect(username.toLowerCase(), chatPassword, onConnectCallback)
  }, [fsUser.cisId, onConnectCallback, tempConnection])

  const disconnect = useCallback(() => {
    connection?.disconnect()
    clearCache()
    dispatch({ type: 'LOGOUT' })
  }, [clearCache, connection, dispatch])

  return { connect, disconnect, connection }
}
